/**
 * Fullscreen PDP — "there's more below" affordance for the details column.
 *
 * The details column is capped to the media column's height and scrolls its
 * own overflow (see `snippets/product-information-content.liquid`). A thin
 * scrollbar alone is too quiet a signal that there's more copy below the fold
 * of the column, so the stylesheet fades the bottom edge out whenever there
 * is. All this module does is keep `data-pi-fs-more` accurate — the fade
 * itself is CSS.
 *
 * It has to be JS rather than a static gradient because the fade must clear
 * once you reach the end, otherwise the final line of copy looks permanently
 * washed out — which reads as a rendering bug rather than an affordance.
 *
 * With this module absent no attribute is ever set, so the column simply has
 * no fade. Nothing is hidden or clipped either way.
 */

const DETAILS_SELECTOR = '.product-information__grid--fullscreen .product-details';

/** Sub-pixel slack so a fully-scrolled column doesn't keep the fade on. */
const EPSILON = 2;

/** @param {HTMLElement} details */
const update = (details) => {
  const hasMore = details.scrollTop + details.clientHeight < details.scrollHeight - EPSILON;
  if (hasMore) {
    details.setAttribute('data-pi-fs-more', 'true');
  } else {
    details.removeAttribute('data-pi-fs-more');
  }
};

/** @param {HTMLElement} details */
const watch = (details) => {
  if (details.dataset.piFsFadeInit === '1') return;
  details.dataset.piFsFadeInit = '1';

  let frame = /** @type {number | null} */ (null);
  const schedule = () => {
    if (frame != null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      update(details);
    });
  };

  details.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  // Late-loading images inside the column change its content height.
  window.addEventListener('load', schedule, { once: true });

  // The column's own height is fixed by CSS, so a ResizeObserver on it would
  // never fire. Watch its children — they're what grows when a taller tab
  // panel opens, an accordion expands, or a variant swap brings new copy.
  const resizeObserver = new ResizeObserver(schedule);
  const observeChildren = () => {
    for (const child of details.children) resizeObserver.observe(child);
  };
  observeChildren();

  // A variant change can replace the column's contents wholesale, taking the
  // observed children with it.
  new MutationObserver(() => {
    observeChildren();
    schedule();
  }).observe(details, { childList: true });

  update(details);
};

const init = () => {
  for (const details of document.querySelectorAll(DETAILS_SELECTOR)) {
    if (details instanceof HTMLElement) watch(details);
  }
};

init();

// Section re-renders (variant changes, theme editor edits) can replace the
// whole subtree, taking the initialised column with it.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const element = /** @type {Element} */ (node);
      if (element.matches?.(DETAILS_SELECTOR) || element.querySelector?.(DETAILS_SELECTOR)) {
        init();
        return;
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });
